// Roster slot definition and slot assignment — the one place both live.
//
// This module is deliberately dependency-free. buildRosterSlots used to live in
// engine/simulation.js, which drags the entire bid engine (archetypes, tiers,
// auction, ADP) in behind it; simulation.js re-exports it from here so every
// existing `import { buildRosterSlots } from './engine/simulation'` still works.
// The move exists so non-UI consumers — api/email/results.js renders the same
// roster card into an email — can reuse this logic without importing the engine.

// FLEX/SFLEX eligibility — the same two lists fillRosterSlots assigns by and
// auction.js draftablePoolCounts sizes the pool by. Exported so a fourth copy
// doesn't get hand-written somewhere else.
export const FLEX_ELIGIBLE = ["RB", "WR", "TE"];
export const SFLEX_ELIGIBLE = ["QB", "RB", "WR", "TE"];

// Which positions can this league actually roster?
//
// A position qualifies if it has its own starting slot, or if it is eligible for
// a FLEX/SFLEX slot the league carries. DEF and K are eligible for neither flex
// type, so DEF: 0 means the league cannot roster a defense at all. Bench slots
// deliberately do NOT qualify a position — a bench adds depth at positions that
// are already startable, it does not make a useless position rosterable. (This
// is the "BN5 Houston Texans $1 in a NO DEF league" bug.)
//
// Missing key ⇒ present. Old saved leagues predate some position keys (see the
// migration note in CLAUDE.md), and the failure modes are asymmetric: wrongly
// hiding a position empties a user's draft board, wrongly showing one costs a
// row in a list. Only an EXPLICIT 0 hides. Note this is a deliberate divergence
// from draftablePoolCounts, which reads `P.DEF ?? 0` because sizing a pool from
// an absent key should yield nothing rather than a phantom position.
export function rosterablePositions(positions = {}) {
  const out = new Set();
  for (const pos of ["QB", "RB", "WR", "TE", "DEF", "K"]) {
    if ((positions[pos] ?? 1) > 0) out.add(pos);
  }
  if ((positions.FLEX || 0) > 0) FLEX_ELIGIBLE.forEach(p => out.add(p));
  if ((positions.SFLEX || 0) > 0) SFLEX_ELIGIBLE.forEach(p => out.add(p));
  return out;
}

// Per-position STARTER requirements: a position's own slot count, nothing else.
// Flex slots are not a per-position requirement — a league with 1 FLEX does not
// require a 3rd RB *and* a 4th WR *and* a 2nd TE — so they are excluded here.
//
// Missing key ⇒ no requirement (the opposite default from rosterablePositions,
// and permissive in the same direction: an absent key must never manufacture a
// requirement that can never be satisfied, which is exactly how a hardcoded
// DEF: 1 soft-locked a no-DEF league).
export function starterRequirements(positions = {}) {
  const reqs = {};
  for (const pos of ["QB", "RB", "WR", "TE", "DEF", "K"]) {
    const n = positions[pos] ?? 0;
    if (n > 0) reqs[pos] = n;
  }
  return reqs;
}

export function buildRosterSlots(positions) {
  const slots = [];
  const order = ["QB", "RB", "WR", "TE", "FLEX", "SFLEX", "DEF", "K", "BN"];
  for (const pos of order) {
    const count = positions[pos] || 0;
    for (let i = 0; i < count; i++) {
      const label = count > 1 ? `${pos}${i + 1}` : pos;
      slots.push({ id: `${pos.toLowerCase()}${i + 1}`, label, pos });
    }
  }
  return slots;
}

export function fillRosterSlots(roster, rosterSlots) {
  const filled = {};
  const posQueues = {};

  roster.forEach(pick => {
    const pos = pick.player.pos;
    if (!posQueues[pos]) posQueues[pos] = [];
    posQueues[pos].push(pick);
  });
  Object.values(posQueues).forEach(q => q.sort((a, b) => b.price - a.price));

  rosterSlots.forEach(slot => {
    if (slot.pos !== "FLEX" && slot.pos !== "BN" && slot.pos !== "SFLEX") {
      const q = posQueues[slot.pos];
      if (q && q.length > 0) filled[slot.id] = q.shift();
    }
  });

  const flexOrder = ["RB", "WR", "TE"];
  rosterSlots.filter(s => s.pos === "FLEX").forEach(slot => {
    for (const pos of flexOrder) {
      const q = posQueues[pos];
      if (q && q.length > 0) {
        filled[slot.id] = q.shift();
        break;
      }
    }
  });

  const sflexOrder = ["QB", "RB", "WR", "TE"];
  rosterSlots.filter(s => s.pos === "SFLEX").forEach(slot => {
    for (const pos of sflexOrder) {
      const q = posQueues[pos];
      if (q && q.length > 0) {
        filled[slot.id] = q.shift();
        break;
      }
    }
  });

  const allRemaining = [];
  Object.values(posQueues).forEach(q => allRemaining.push(...q));
  allRemaining.sort((a, b) => b.price - a.price);
  rosterSlots.filter(s => s.pos === "BN").forEach(slot => {
    if (allRemaining.length > 0) filled[slot.id] = allRemaining.shift();
  });

  return filled;
}
